import React, {useEffect, useRef, useState} from "react";
import {Radio, Spin, Table, Typography} from "antd";
import {NotificationOutlined} from "@ant-design/icons";
import dayjs from "dayjs";

import PromotionAddDrawer from "./PromotionAddDrawer";
import PromotionEditDrawer from "./PromotionEditDrawer";
import {getAllPromotions} from "../../../helpers/promotion";
import {messageType, showMessages} from "../../../helpers/message";
import EngageCurrency from "../../../components/billings/EngageCurrency";
import {dateFormat} from "../../../lib/time";
import PageTitle from "../../../components/page/PageTitle";
import {userHasRole} from "../../../lib/role";

import "./Promotions.less";

const Promotions = () => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);
    const {innerWidth} = window;
    const isMarketing = userHasRole('staff', 'marketing') || userHasRole('staff', 'developer');

    // Pagination states.
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(10);

    // Promotions table states.
    const [promotions, setPromotions] = useState([]);
    const [activeTabFilter, setActiveTabFilter] = useState('all');
    const [tableRefresh, setTableRefresh] = useState(false);

    const promoEditComponent = (promo) => {
        return (
            <PromotionEditDrawer
                promoID={promo.id}
                oldPromoName={promo.name}
                oldPromoCode={promo.promo_code}
                oldPromoStartedAt={dayjs(promo.started_at).tz()}
                oldPromoEndedAt={promo.ended_at ? dayjs(promo.ended_at).tz() : null}
                oldPromoDefaultAmount={Number(promo.default_amount)}
                oldPromoCreditEndDate={promo.credits_default_ended_at ? dayjs(promo.credits_default_ended_at).tz() : null}
                oldPromoCreditDuration={promo.credits_default_duration}
                oldPromoMaxCredits={promo.credits_max_num_total}
                oldPromoMaxCreditsAccount={promo.credits_max_num_account}
                setTableRefresh={setTableRefresh}
            />
        );
    }

    const columns = [
        {
            title: <Typography.Text strong>Name</Typography.Text>,
            dataIndex: 'name',
            key: 'name',
            render: name => {
                return <span className="long-text-wrap">{name}</span>
            },
        },
        {
            title: <Typography.Text strong>Promo Code</Typography.Text>,
            dataIndex: 'promo_code',
            key: 'promo_code',
            render: promo_code => {
                return <span className="long-text-wrap">{promo_code || 'N/A'}</span>
            },
        },
        {
            title: <Typography.Text strong>Start Date</Typography.Text>,
            dataIndex: 'started_at',
            key: 'started_at',
            render: started_at => {
                return <span className="long-text-wrap">{dayjs(started_at).tz().format(dateFormat)}</span>
            },
        },
        {
            title: <Typography.Text strong>End Date</Typography.Text>,
            dataIndex: 'ended_at',
            key: 'ended_at',
            render: ended_at => {
                return <span
                    className="long-text-wrap">{ended_at ? dayjs(ended_at).tz().format(dateFormat) : 'N/A'}</span>
            },
        },
        {
            title: <Typography.Text strong>Value</Typography.Text>,
            dataIndex: 'default_amount',
            key: 'default_amount',
            render: default_amount => {
                return <EngageCurrency amount={parseFloat(default_amount)}/>
            },
        },
        {
            title: <Typography.Text strong>Used (#)</Typography.Text>,
            dataIndex: 'credits_used_num',
            key: 'credits_used_num',
            render: credits_used_num => {
                return <span className="long-text-wrap">{credits_used_num}</span>
            },
        },
        {
            title: <Typography.Text strong>Action</Typography.Text>,
            key: 'action',
            render: (promo) => promoEditComponent(promo)
        },
    ];

    const expandedRowRender = (record) => {
        let expiration;
        let defaultDuration = record.credits_default_duration;

        if (defaultDuration) {
            // Set expiration as the default credits expiration duration if exists.
            expiration = `${defaultDuration} ${defaultDuration === 1 ? 'day' : 'days'}`;
        } else {
            // Set expiration as credit default ended at date if duration is not provided.
            expiration = dayjs(record.credits_default_ended_at).tz().format(dateFormat);
        }

        const expandRows = [
            <li key={0} className="expanded-row-list-item">
                <Typography.Text className="expanded-row-list-title" strong>Promo Code: </Typography.Text>
                {record.promo_code || 'N/A'}
            </li>,
            <li key={1} className="expanded-row-list-item">
                <Typography.Text className="expanded-row-list-title" strong>Start Date: </Typography.Text>
                {dayjs(record.started_at).tz().format(dateFormat)}
            </li>,
            <li key={2} className="expanded-row-list-item">
                <Typography.Text className="expanded-row-list-title" strong>End Date: </Typography.Text>
                {record.ended_at ? dayjs(record.ended_at).tz().format(dateFormat) : 'N/A'}
            </li>,
            <li key={3} className="expanded-row-list-item">
                <Typography.Text className="expanded-row-list-title" strong>Value: </Typography.Text>
                <EngageCurrency amount={parseFloat(record.default_amount)}/>
            </li>,
            <li key={4} className="expanded-row-list-item">
                <Typography.Text className="expanded-row-list-title" strong>Expiration: </Typography.Text>
                {expiration}
            </li>,
            <li key={5} className="expanded-row-list-item">
                <Typography.Text className="expanded-row-list-title" strong>Max Credits: </Typography.Text>
                {record.credits_max_num_total || 'N/A'}
            </li>,
            <li key={6} className="expanded-row-list-item">
                <Typography.Text className="expanded-row-list-title" strong>Max Credits Per Account: </Typography.Text>
                {record.credits_max_num_account || 'N/A'}
            </li>,
            <li key={7} className="expanded-row-list-item">
                <Typography.Text className="expanded-row-list-title" strong>Used ($): </Typography.Text>
                <EngageCurrency amount={parseFloat(record.default_amount) * record.credits_used_num}/>
            </li>,
            <li key={8} className="expanded-row-list-item">
                <Typography.Text className="expanded-row-list-title" strong>Used (#): </Typography.Text>
                {record.credits_used_num}
            </li>,
            <li key={9} className="expanded-row-list-item">
                <Typography.Text className="expanded-row-list-title" strong>Total Value Redeemed: </Typography.Text>
                <EngageCurrency amount={parseFloat(record.credits_redeemed_amount)}/>
            </li>,
            <li key={10} className="expanded-row-list-item">
                <Typography.Text className="expanded-row-list-title" strong>Total Value Expired: </Typography.Text>
                <EngageCurrency amount={parseFloat(record.credits_expired_amount)}/>
            </li>,
            <li key={11} className="expanded-row-list-item">
               <Typography.Text className="expanded-row-list-title" strong>Action: </Typography.Text>
               <div className="mobile-edit-button">
                   {promoEditComponent(record)}
               </div>
            </li>
        ];

        // If desktop, only show keys 4 - 6 and 8 - 10. (Skip 7, it's a column in the main table).
        let desktopExpandedRows = expandRows.slice(4, 11);
        desktopExpandedRows.splice(4, 1);
        // If mobile window width, show all expandRows.
        return (
            <ul className="expanded-row-list">
                {innerWidth <= 540 ? expandRows : desktopExpandedRows}
            </ul>
        )
    }

    const handleTabChange = (e) => {
        setLoading(true);
        setActiveTabFilter(e.target.value);
        setPage(1);
        setLoading(false);
    }

    // Handle mount and update.
    useEffect(() => {
        const fetchPromotions = async () => {
            setLoading(true);
            try {
                const abortSignal = abortSignalRef.current;
                const response = await getAllPromotions(page, perPage, activeTabFilter, abortSignal);
                if (response && abortSignal.aborted === false) {
                    setPromotions(response.data);
                    setTotalRows(response.meta.total);
                }
                setLoading(false);
            } catch (e) {
                showMessages(e.message, messageType.error);
                setLoading(false);
                if(e.status_code === 404) window.location.replace('/404');
            }
            setTableRefresh(false);
        }
        fetchPromotions();
    }, [page, perPage, activeTabFilter, tableRefresh]);

    //Pagination logic.
    const onChange = (page, perPage) => {
        setPage(page);
        setPerPage(perPage);
    }

    const paginationAttr = {
        current: page,
        pageSize: perPage,
        total: totalRows,
        onChange,
        showSizeChanger: true,
        simple: innerWidth <= 767,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        onShowSizeChange: onChange,
    }

    return (
        <>
            <Spin spinning={loading}>
                <div className="promotions-page-title">
                    <PageTitle title={'Promotions'} icon={<NotificationOutlined/>}/>
                </div>
                {!isMarketing
                    ?
                    <Typography.Text className="role-disclaimer-text">
                        Sorry, you do not have the required permissions to view this page.
                    </Typography.Text>
                    :
                    <Table
                        rowKey="id"
                        dataSource={promotions}
                        columns={innerWidth <= 540 ? columns.slice(0, 1) : columns} // Only show 'name' column in mobile.
                        expandable={{expandedRowRender}}
                        bordered={false}
                        pagination={paginationAttr}
                        title={() =>
                            <div className="promotions-tab-bar">
                                <div className="mobile-title-button">
                                    <div>
                                        <div className="promotions-tab-bar-title">Promotions</div>
                                    </div>
                                    {innerWidth <= 540 ? // When mobile, add button must be on top of radio buttons.
                                        <>
                                            <PromotionAddDrawer
                                                addButtonText={innerWidth < 340 ? 'Add' : 'Add New'}
                                                setTableRefresh={setTableRefresh}
                                            />
                                        </>
                                        : null
                                    }
                                </div>
                                <div className="promotions-table-btn-container">
                                    <Radio.Group
                                        className="promotions-table-filter-btn-container"
                                        value={activeTabFilter}
                                        onChange={handleTabChange}
                                    >
                                        <Radio.Button value={'all'}>All</Radio.Button>
                                        <Radio.Button
                                            value={'future'}>{innerWidth < 340 ? 'Ftr' : 'Future'}</Radio.Button>
                                        <Radio.Button
                                            value={'current'}>{innerWidth < 340 ? 'Crt' : 'Current'}</Radio.Button>
                                        <Radio.Button
                                            value={'expired'}>{innerWidth < 340 ? 'Exp' : 'Expired'}</Radio.Button>
                                    </Radio.Group>
                                    {innerWidth > 540 ?
                                        <>
                                            <PromotionAddDrawer
                                                addButtonText={innerWidth > 450 && innerWidth < 600 ? '' : 'Add New'}
                                                setTableRefresh={setTableRefresh}
                                            />
                                            {/*Show only plus sign when button gets squished and hidden.*/}
                                        </>
                                        : null
                                    }
                                </div>
                            </div>}
                    />}
            </Spin>
        </>
    )
}

export default Promotions;

Promotions.propTypes = {};
